import React, { useState, useEffect } from 'react';
import { useChatbotCRMIntegration } from '../../hooks/useChatbotCRMIntegration';
import { useDesignColors } from '../../hooks/useDesignColors';
import { Check, Info, ExternalLink, AlertTriangle } from 'lucide-react';

interface SalesforceConfig {
  clientId: string;
  clientSecret: string;
  instanceUrl: string;
}

interface SalesforceFormProps {
  config: SalesforceConfig;
  enabled: boolean;
  userId: string;
}

export function SalesforceForm({ config, enabled, userId }: SalesforceFormProps) {
  const { updateSalesforceConfig, loading, error, clearError } = useChatbotCRMIntegration();
  const { colors } = useDesignColors();
  const [formState, setFormState] = useState<SalesforceConfig>({
    clientId: '',
    clientSecret: '',
    instanceUrl: '',
  });
  const [isEnabled, setIsEnabled] = useState(enabled);
  const [success, setSuccess] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setFormState({
      clientId: config.clientId || '',
      clientSecret: config.clientSecret || '',
      instanceUrl: config.instanceUrl || '',
    });
    setIsEnabled(enabled);
  }, [config, enabled]);

  // Vérifier quels champs sont en erreur d'après le message d'erreur
  useEffect(() => {
    if (error && error.includes('Missing required fields')) {
      const missingFields: Record<string, boolean> = {};
      
      if (error.includes('Client ID')) {
        missingFields.clientId = true;
      }
      if (error.includes('Client Secret')) {
        missingFields.clientSecret = true;
      }
      if (error.includes('Instance URL')) {
        missingFields.instanceUrl = true;
      }
      
      setFieldErrors(missingFields);
    } else {
      setFieldErrors({});
    }
  }, [error]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Effacer les erreurs de ce champ spécifique
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: false }));
    }
    
    // Clear previous success message and errors on input change
    setSuccess(false);
    clearError();
  };

  const handleToggle = () => {
    setIsEnabled(!isEnabled);
    setSuccess(false);
    clearError();
    setFieldErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation côté client avant d'envoyer
    if (isEnabled) {
      const missingFields: Record<string, boolean> = {};
      let hasErrors = false;
      
      if (!formState.clientId) {
        missingFields.clientId = true;
        hasErrors = true;
      }
      if (!formState.clientSecret) {
        missingFields.clientSecret = true;
        hasErrors = true;
      }
      if (!formState.instanceUrl) {
        missingFields.instanceUrl = true;
        hasErrors = true;
      }
      
      if (hasErrors) {
        setFieldErrors(missingFields);
        return;
      }
    }
    
    try {
      await updateSalesforceConfig({
        client_id: formState.clientId,
        client_secret: formState.clientSecret,
        instance_url: formState.instanceUrl,
        is_enabled: isEnabled
      }, userId);
      
      setSuccess(true);
      setFieldErrors({});
      // Reset success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating Salesforce config:', err);
    }
  };

  const gradientStyle = {
    background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
  };

  const getInputClassName = (fieldName: string) => {
    const baseClass = "w-full px-3 py-2.5 bg-gray-800/70 border rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-gray-800 focus:ring-opacity-50";
    if (fieldErrors[fieldName]) {
      return `${baseClass} border-red-500`;
    }
    return `${baseClass} border-gray-700`;
  };

  return (
    <div className="space-y-6">
      {/* Setup Instructions Toggle */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-white">Configuration</h3>
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-md transition-all"
          style={{
            background: showInstructions 
              ? `linear-gradient(135deg, ${colors.gradientStart}30, ${colors.gradientEnd}30)` 
              : 'transparent',
            border: `1px solid ${colors.gradientStart}50`,
            color: showInstructions ? colors.gradientStart : 'white'
          }}
        >
          <Info className="w-4 h-4" />
          {showInstructions ? 'Hide Setup Guide' : 'Setup Guide'}
        </button>
      </div>

      {/* Setup Instructions */}
      {showInstructions && (
        <div className="bg-white/5 rounded-lg p-4 border border-white/10 space-y-3">
          <h4 className="font-medium text-white">How to Set Up Salesforce Integration</h4>
          
          <div className="space-y-3 text-sm text-gray-300">
            <p className="leading-relaxed">
              Follow these steps to integrate your chatbot with Salesforce CRM:
            </p>
            
            <ol className="list-decimal pl-5 space-y-4">
              <li>
                <p className="font-medium text-white">Create a Connected App in Salesforce</p>
                <p className="text-xs text-gray-400 mt-1">Log in to your Salesforce account and navigate to Setup → App Manager → New Connected App</p>
              </li>
              
              <li>
                <p className="font-medium text-white">Configure OAuth Settings</p>
                <ul className="list-disc pl-5 space-y-1 mt-1 text-xs text-gray-400">
                  <li>Enable OAuth settings</li>
                  <li>Set the callback URL to: <code className="bg-gray-800 px-1 rounded">https://your-app-domain.com/auth/callback</code></li>
                  <li>Select the required OAuth scopes: manage_user_data, api, refresh_token</li>
                </ul>
              </li>
              
              <li>
                <p className="font-medium text-white">Get Your Credentials</p>
                <p className="text-xs text-gray-400 mt-1">After saving, Salesforce will generate a Consumer Key (Client ID) and Consumer Secret (Client Secret). Copy these values.</p>
              </li>
              
              <li>
                <p className="font-medium text-white">Enter Your Credentials</p>
                <p className="text-xs text-gray-400 mt-1">Enter your Salesforce Instance URL, Client ID, and Client Secret in the form below.</p>
              </li>
            </ol>
            
            <a 
              href="https://developer.salesforce.com/docs/atlas.en-us.api_rest.meta/api_rest/intro_understanding_authentication.htm" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center mt-2 text-xs hover:underline transition-all"
              style={{ color: colors.gradientStart }}
            >
              Read Salesforce API Documentation
              <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-400">
            Enter your Salesforce API credentials to sync leads
          </p>
          <div className="flex items-center">
            <span className="mr-3 text-sm font-medium text-gray-300">Enable</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isEnabled}
                onChange={handleToggle}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-offset-1 peer-focus:ring-offset-gray-800 peer-focus:ring-opacity-50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all duration-300"
                style={{ 
                  background: isEnabled ? gradientStyle.background : 'rgba(55, 65, 81, 0.7)'
                }}
              ></div>
            </label>
          </div>
        </div>

        {isEnabled && Object.keys(fieldErrors).length > 0 && (
          <div className="flex items-start gap-2 bg-amber-500/10 text-amber-400 p-3 rounded-md text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Required fields are missing</p>
              <p className="text-xs text-amber-400/80 mt-1">Please fill in all the highlighted fields to enable the integration.</p>
            </div>
          </div>
        )}

        {success && (
          <div className="flex items-center gap-2 bg-green-500/10 text-green-400 p-3 rounded-md text-sm">
            <Check className="w-4 h-4" />
            Salesforce configuration saved successfully!
          </div>
        )}

        {error && !error.includes('Missing required fields') && (
          <div className="bg-red-500/10 text-red-400 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5">
          <div className="space-y-1.5">
            <label htmlFor="instanceUrl" className="block text-sm font-medium text-gray-300">
              Instance URL {isEnabled && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              id="instanceUrl"
              name="instanceUrl"
              value={formState.instanceUrl}
              onChange={handleInputChange}
              placeholder="https://yourinstance.salesforce.com"
              className={getInputClassName('instanceUrl')}
              style={{}}
              required={isEnabled}
            />
            {fieldErrors.instanceUrl && (
              <p className="text-xs text-red-400 mt-1">Instance URL is required</p>
            )}
            <p className="text-xs text-gray-500">
              Your Salesforce instance URL, e.g., https://mycompany.my.salesforce.com
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="clientId" className="block text-sm font-medium text-gray-300">
              Client ID {isEnabled && <span className="text-red-400">*</span>}
            </label>
            <input
              type="text"
              id="clientId"
              name="clientId"
              value={formState.clientId}
              onChange={handleInputChange}
              placeholder="Enter your Client ID"
              className={getInputClassName('clientId')}
              style={{}}
              required={isEnabled}
            />
            {fieldErrors.clientId && (
              <p className="text-xs text-red-400 mt-1">Client ID is required</p>
            )}
            <p className="text-xs text-gray-500">
              The Consumer Key from your Salesforce Connected App
            </p>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="clientSecret" className="block text-sm font-medium text-gray-300">
              Client Secret {isEnabled && <span className="text-red-400">*</span>}
            </label>
            <input
              type="password"
              id="clientSecret"
              name="clientSecret"
              value={formState.clientSecret}
              onChange={handleInputChange}
              placeholder="Enter your Client Secret"
              className={getInputClassName('clientSecret')}
              style={{}}
              required={isEnabled}
            />
            {fieldErrors.clientSecret && (
              <p className="text-xs text-red-400 mt-1">Client Secret is required</p>
            )}
            <p className="text-xs text-gray-500">
              The Consumer Secret from your Salesforce Connected App
            </p>
          </div>
        </div>

        <div className="pt-4">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center py-2.5 px-4 rounded-md shadow-lg text-sm font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: gradientStyle.background,
              boxShadow: `0 4px 15px ${colors.gradientStart}30`
            }}
          >
            {loading ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
} 